'use client';

import { Footer } from '@/components/public/footer';
import { NavPreview } from '@/components/public/nav-preview';
import { NewsletterSubscribeForm } from '@/components/public/newsletter-subscribe-form';
import { useState } from 'react';
import { useScrollReveal } from '@/lib/hooks/use-scroll-reveal';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import Image from 'next/image';
import { Link } from '@/i18n/navigation';
import { useTranslations } from 'next-intl';
import { HomepageReviews } from '@/components/public/homepage-reviews';
import type { GoogleReviewsData } from '@/types/reviews';

function FAQAccordion() {
  const t = useTranslations('design.homepage.faq');
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    { question: t('q1'), answer: t('a1') },
    { question: t('q2'), answer: t('a2') },
    { question: t('q3'), answer: t('a3') }
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="space-y-4">
      {faqs.map((faq, index) => (
        <div
          key={index}
          className="bg-white rounded-2xl shadow-lg overflow-hidden observe-me slide-up transition-all duration-300 hover:shadow-2xl"
          style={{ animationDelay: `${index * 0.15}s` }}
        >
          <button
            onClick={() => toggleFAQ(index)}
            className="w-full px-8 py-6 text-left flex items-center justify-between gap-4 hover:bg-[#faf8f6] transition-colors duration-300"
          >
            <span className="font-poppins text-xl md:text-2xl font-semibold text-gray-900 pr-4">
              {faq.question}
            </span>
            <div
              className={`flex-shrink-0 w-10 h-10 rounded-full bg-[#801515] flex items-center justify-center text-white transition-transform duration-500 ${
                openIndex === index ? 'rotate-180' : ''
              }`}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>
          <div
            className={`transition-all duration-500 ease-in-out ${
              openIndex === index ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
            }`}
            style={{ overflow: 'hidden' }}
          >
            <div className="px-8 pb-6 pt-2">
              <div className="w-16 h-1 bg-[#801515] mb-4" />
              <p className="text-lg text-gray-600 leading-relaxed">
                {faq.answer}
              </p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function NewsAndOffers() {
  const t = useTranslations('design.newsAndOffers');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [direction, setDirection] = useState<'next' | 'prev'>('next');

  const offers = [
    {
      id: 'autumn-break-2026',
      badge: t('autumnBreak.badge'),
      titleAccent: t('autumnBreak.titleAccent'),
      titleSub: t('autumnBreak.titleSub'),
      description: t('autumnBreak.description'),
      descriptionDate: t('autumnBreak.descriptionDate'),
      cta: t('autumnBreak.cta'),
      footnote: t('autumnBreak.footnote'),
      publishDate: t('autumnBreak.publishDate'),
      image: '/design-images/uploads/1788876190988-1kmpij-c1_06.jpg',
      imageAlt: t('autumnBreak.imageAlt'),
      imageFirst: false,
      ctaHref: '/offers/autumn-2026',
      isExternal: false
    },
    {
      id: 'new-years-eve-2027',
      badge: t('newYearsEve.badge'),
      titleAccent: t('newYearsEve.titleAccent'),
      titleSub: t('newYearsEve.titleSub'),
      description: t('newYearsEve.description'),
      descriptionDate: t('newYearsEve.descriptionDate'),
      cta: t('newYearsEve.cta'),
      footnote: t('newYearsEve.footnote'),
      publishDate: t('newYearsEve.publishDate'),
      image: '/design-images/uploads/1786541739440-x6toss-chatgpt-image-11-aug.-2026-09_03_51.jpg',
      imageAlt: t('newYearsEve.imageAlt'),
      imageFirst: false,
      ctaHref: '/offers/new-years-eve-2027',
      isExternal: false
    },
    {
      id: 'myrmidone-certification-2026',
      badge: t('myrmidoneCertification.badge'),
      titleAccent: t('myrmidoneCertification.titleAccent'),
      titleSub: t('myrmidoneCertification.titleSub'),
      description: t('myrmidoneCertification.description'),
      footnote: t('myrmidoneCertification.footnote'),
      publishDate: t('myrmidoneCertification.publishDate'),
      image: '/design-images/uploads/1790166721479-58thmk-whatsapp-image-2026-09-23-at-15.31.14.jpg',
      imageAlt: t('myrmidoneCertification.imageAlt'),
      imageFirst: false,
      isExternal: false
    },
    {
      id: 'gift-vouchers',
      badge: t('giftVouchers.badge'),
      titleAccent: t('giftVouchers.titleAccent'),
      titleSub: t('giftVouchers.titleSub'),
      description: t('giftVouchers.description'),
      cta: t('giftVouchers.cta'),
      footnote: t('giftVouchers.footnote'),
      publishDate: t('giftVouchers.publishDate'),
      image: '/design-images/uploads/1777555438216-0082ds1_9891-enhanced-nr.jpg',
      imageAlt: t('giftVouchers.imageAlt'),
      imageFirst: false,
      ctaHref: '/gift-vouchers',
      isExternal: false
    },
    {
      id: 'top-hotel-awards',
      badge: t('topHotelAwards.badge'),
      titleAccent: t('topHotelAwards.title'),
      titleSub: t('topHotelAwards.subtitle'),
      description: t('topHotelAwards.description'),
      image: '/design-images/uploads/1781250193394-3i85fb-copy-of-last-week-tophotel-awards-nominee.png',
      imageAlt: t('topHotelAwards.imageAlt'),
      publishDate: t('topHotelAwards.publishDate'),
      imageFirst: false,
      isExternal: true
    }
  ];

  const goToNext = () => {
    if (isAnimating) return;
    setDirection('next');
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentSlide((prev) => (prev + 1) % offers.length);
      setTimeout(() => setIsAnimating(false), 50);
    }, 400);
  };

  const goToPrev = () => {
    if (isAnimating) return;
    setDirection('prev');
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentSlide((prev) => (prev - 1 + offers.length) % offers.length);
      setTimeout(() => setIsAnimating(false), 50);
    }, 400);
  };

  const goToSlide = (index: number) => {
    if (isAnimating || index === currentSlide) return;
    setDirection(index > currentSlide ? 'next' : 'prev');
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentSlide(index);
      setTimeout(() => setIsAnimating(false), 50);
    }, 400);
  };

  const offer = offers[currentSlide];

  return (
    <div className="relative">
      {/* Yellow Oval Badge - Alternates with photo position.
          Lives outside the card's overflow-hidden so its mobile pop-above-the-edge
          (-top-4) isn't clipped; sits inside the card on desktop (md:top-4). */}
      <div className={`absolute -top-4 md:top-4 px-6 py-2 lg:px-8 lg:py-3 bg-yellow-300 rounded-full flex items-center justify-center z-30 shadow-lg yellow-bubble-zoom ${offer.imageFirst ? 'left-4' : 'right-4'}`}>
        <span className="font-poppins text-sm lg:text-base font-bold text-[#801515] text-center">
          {t('hero.badge')}
        </span>
      </div>

      <div
        className="observe-me slide-left rounded-3xl overflow-hidden relative"
        style={{
          background: 'linear-gradient(135deg, #fcd34d 0%, #801515 100%)',
          padding: '4px'
        }}
      >
      <div className="bg-white rounded-[calc(1.5rem-4px)] overflow-hidden">
        {/* Carousel Container */}
        <div className="relative">

        {/* Grid Section with Animation */}
        <section
          key={currentSlide}
          className={`grid lg:grid-cols-5 min-h-[80vh] rounded-none overflow-hidden transition-all duration-500 ease-out ${
            isAnimating
              ? direction === 'next'
                ? 'opacity-0 -translate-x-12'
                : 'opacity-0 translate-x-12'
              : 'opacity-100 translate-x-0'
          }`}
        >
          {/* Image Side */}
          <div
            className={`lg:col-span-3 relative h-[50vh] lg:h-auto overflow-hidden bg-gray-100 order-2 rounded-none ${offer.imageFirst ? 'lg:order-1' : 'lg:order-2'}`}
          >
            <Image
              src={offer.image}
              alt={offer.imageAlt}
              fill
              className="object-cover h-full w-full"
              style={{ objectPosition: 'center' }}
            />
          </div>

          {/* Content Side */}
          <div
            className={`lg:col-span-2 flex items-center justify-center design-content-pad lg:p-12 xl:p-16 bg-gradient-to-br from-[#faf5f5] via-[#fef7f0] to-[#faf5f5] order-1 ${offer.imageFirst ? 'lg:order-2' : 'lg:order-1'}`}
          >
            <div className="max-w-lg">
              <div className="inline-block px-6 py-2 bg-[#801515] text-white text-sm font-semibold rounded-full mb-6 uppercase tracking-wider">
                {offer.badge}
              </div>

              <h2 className="font-poppins text-lg sm:text-2xl md:text-3xl xl:text-4xl font-bold mb-4 leading-[1.1]">
                <span className="text-[#801515]">{offer.titleAccent}</span>
                <br />
                <span className="text-gray-800 text-xl md:text-2xl xl:text-3xl font-normal mt-3 block">
                  {offer.titleSub}
                </span>
              </h2>

              <div className="w-16 h-1 bg-[#801515] mb-6" />

              <p className="text-lg text-gray-700 leading-relaxed mb-6 font-light italic">
                {offer.descriptionDate && (
                  <>
                    <span className="font-bold">{offer.descriptionDate}</span>
                    <br />
                  </>
                )}
                {offer.description}
              </p>

              {offer.footnote && (
                <p className="text-lg xl:text-xl text-gray-600 leading-relaxed mb-6">
                  {offer.footnote}
                </p>
              )}

              {offer.cta && (
                <div>
                  {offer.isExternal ? (
                    <a
                      href={offer.ctaHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group w-full lg:w-auto px-[26px] py-5 bg-[#801515] hover:bg-[#601010] text-white font-serif text-xl rounded-full transition-all duration-500 flex items-center justify-center gap-3 shadow-lg hover:shadow-xl hover:shadow-[#801515]/30"
                    >
                      {offer.cta}
                      <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
                    </a>
                  ) : (
                    <Link
                      href={offer.ctaHref}
                      className="group w-full lg:w-auto px-[26px] py-5 bg-[#801515] hover:bg-[#601010] text-white font-serif text-xl rounded-full transition-all duration-500 flex items-center justify-center gap-3 shadow-lg hover:shadow-xl hover:shadow-[#801515]/30"
                    >
                      {offer.cta}
                      <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  )}
                </div>
              )}

              {offer.publishDate && (
                <p className="text-xs text-gray-400 mt-6 text-center lg:text-left">
                  {offer.publishDate}
                </p>
              )}
            </div>
          </div>
        </section>

        {/* Navigation Arrows */}
        <button
          onClick={goToPrev}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white border-2 border-[#801515] shadow-lg hover:shadow-xl flex items-center justify-center text-[#801515] hover:bg-[#801515] hover:text-white transition-all duration-300 transform hover:scale-110 z-10"
          aria-label="Previous offer"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <button
          onClick={goToNext}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white border-2 border-[#801515] shadow-lg hover:shadow-xl flex items-center justify-center text-[#801515] hover:bg-[#801515] hover:text-white transition-all duration-300 transform hover:scale-110 z-10"
          aria-label="Next offer"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      </div>

        {/* Dots Navigation */}
        <div className="flex justify-center gap-3 py-6">
          {offers.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`transition-all duration-300 rounded-full ${
                index === currentSlide
                  ? 'w-12 h-3 bg-[#801515]'
                  : 'w-3 h-3 bg-gray-300 hover:bg-[#801515]/50'
              }`}
              aria-label={`Go to offer ${index + 1}`}
            />
          ))}
        </div>
      </div>
      </div>
    </div>
  );
}


function FloatingBookNow() {
  const t = useTranslations('rooms');
  const visible = true;

  return (
    <>
      {/* Desktop: floating bottom-right button */}
      <div
        className={`fixed right-8 z-40 hidden md:block transition-all duration-500 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
        style={{ bottom: 'calc(2rem + var(--selection-bar-height))' }}
      >
        <Link
          href="/rooms#rooms-list"
          className="group inline-flex items-center gap-2 px-[26px] py-4 bg-[#801515] hover:bg-[#601010] text-white font-serif text-lg rounded-full shadow-2xl hover:shadow-[0_8px_30px_rgba(128,21,21,0.45)] transition-all duration-300 hover:scale-105"
        >
          {t('bookNow')}
          <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>

      {/* Mobile: sticky bottom bar */}
      <div
        className={`fixed left-0 right-0 md:hidden z-50 transition-all duration-500 ${
          visible ? 'translate-y-0' : 'translate-y-full'
        }`}
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)', bottom: 'var(--selection-bar-height)' }}
      >
        <Link
          href="/rooms#rooms-list"
          className="flex items-center justify-center gap-2 w-full py-4 bg-[#801515] text-white text-base font-bold uppercase tracking-wider border-t border-black/20"
          style={{ boxShadow: '0px -4px 20px rgba(0, 0, 0, 0.15)' }}
        >
          {t('bookNow')}
          <ArrowRight className="w-5 h-5" />
        </Link>
      </div>
    </>
  );
}

export default function HomePage({ reviews }: { reviews?: GoogleReviewsData | null }) {
  const t = useTranslations('design.homepage');
  useScrollReveal();

  return (
    <main className="min-h-screen bg-white text-gray-900">
      <style jsx global>{`
        @keyframes breathe {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.08); }
        }
        @keyframes breatheSlow {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.6; }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        @keyframes blink {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.12); }
        }
        @keyframes zoomInOutBlink {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.15); }
        }
        .image-breathe {
          animation: breathe 8s ease-in-out infinite;
        }
        .yellow-bubble-zoom {
          animation: zoomInOutBlink 1.5s ease-in-out infinite;
        }
        .image-breathe-slow {
          animation: breatheSlow 10s ease-in-out infinite;
        }
        .logo-float {
          animation: float 6s ease-in-out infinite;
        }
        .btn-blink {
          animation: blink 1.5s ease-in-out infinite;
        }
      `}</style>

      <NavPreview />
      <FloatingBookNow />

      {/* Hero Section - Full Width with Fresco Room */}
      <section className="relative h-screen w-full overflow-hidden">
        <div className="absolute inset-0">
          <Image
            src="/design-images/resized/1764882750477-0031DS2_4640.jpg"
            alt="Historic room with fresco"
            fill
            className="object-cover image-breathe-slow"
          />
        </div>
        {/* <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/15 to-white/90" /> */}

        <div className="relative h-full flex flex-col items-center justify-center px-6 pt-48 lg:pt-0 text-center">
          <div className="observe-me slide-up">
            <h1 className="font-poppins text-3xl sm:text-5xl md:text-7xl font-bold text-white mb-6 tracking-tight" style={{ textShadow: '2px 2px 30px rgba(0, 0, 0, 0.5)' }}>
              {t('hero.title')}
            </h1>
            <div className="flex items-center justify-center gap-4 mb-8">
              <div className="h-[2px] w-20 bg-[#801515]" style={{ animation: 'pulse 3s ease-in-out infinite' }} />
              <p className="text-base md:text-lg uppercase tracking-[0.3em] text-white/90" style={{ textShadow: '1px 1px 4px rgba(0, 0, 0, 0.5)' }}>{t('hero.location')}</p>
              <div className="h-[2px] w-20 bg-[#801515]" style={{ animation: 'pulse 3s ease-in-out infinite', animationDelay: '1.5s' }} />
            </div>
            <p className="text-xl sm:text-3xl md:text-4xl text-white/95 max-w-3xl mx-auto font-light" style={{ textShadow: '1px 1px 8px rgba(0, 0, 0, 0.5)' }}>
              {t('hero.subtitle')}
            </p>
          </div>
        </div>

      </section>

      {/* Imagine Section - Asymmetric Layout (Image 3/5, Text 2/5) */}
      <section className="grid lg:grid-cols-5 min-h-screen design-section-gap rounded-3xl overflow-hidden">
        {/* Image Side - 3 columns */}
        <div className="lg:col-span-3 relative h-[50vh] lg:h-auto overflow-hidden bg-gray-100 observe-me slide-left">
          <Image
            src="/design-images/resized/1764858845413-0089DS1_9909-Enhanced-NR.jpg"
            alt="Romantic room interior"
            fill
            className="object-cover image-breathe hover:scale-110 transition-transform duration-700"
          />
        </div>

        {/* Text Side - 2 columns */}
        <div className="lg:col-span-2 flex items-center justify-center design-content-pad lg:p-12 xl:p-16 bg-[#faf8f6]">
          <div className="max-w-lg observe-me slide-right">
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6 leading-[1.1]">
              <span className="text-[#801515]">{t('imagine.heading')}</span>
              <br />
              <span className="text-gray-800 text-xl sm:text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
                {t('imagine.subheading')}
              </span>
            </h2>
            <div className="w-16 h-1 bg-[#801515] mb-6" />
            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed mb-8">
              {t('imagine.body')}
            </p>
            <div>
              <Link href="/rooms" className="group px-[26px] py-5 xl:py-6 bg-[#801515] hover:bg-[#601010] text-white font-serif text-xl xl:text-2xl rounded-full transition-all duration-500 inline-flex items-center gap-3 shadow-lg hover:shadow-xl hover:shadow-[#801515]/30">
                {t('imagine.cta')}
                <ArrowRight className="w-7 h-7 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>


      {/* Choose Your Room - Asymmetric Layout (Text 2/5, Image 3/5) */}
      <section className="grid lg:grid-cols-5 min-h-screen design-section-gap rounded-3xl overflow-hidden">
        {/* Text Side - 2 columns */}
        <div className="lg:col-span-2 flex items-center justify-center design-content-pad lg:p-12 xl:p-16 bg-gradient-to-br from-[#f5f3f0] to-[#faf8f6]">
          <div className="max-w-lg observe-me slide-left">
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6 leading-[1.1]">
              <span className="text-[#801515]">{t('enjoy.heading')}</span>
              <br />
              <span className="text-gray-800 text-xl sm:text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
                {t('enjoy.subheading')}
              </span>
            </h2>
            <div className="w-16 h-1 bg-[#801515] mb-6" />
            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed mb-8" style={{ whiteSpace: 'pre-line' }}>
              {t('enjoy.body')}
            </p>
            <div>
              <Link href="/rooms" className="group px-[26px] py-5 xl:py-6 bg-[#801515] hover:bg-[#601010] text-white font-serif text-xl xl:text-2xl rounded-full transition-all duration-500 inline-flex items-center gap-3 shadow-lg hover:shadow-xl hover:shadow-[#801515]/30">
                {t('enjoy.cta')}
                <ArrowRight className="w-7 h-7 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>

        {/* Image Side - 3 columns */}
        <div className="lg:col-span-3 relative h-[50vh] lg:h-auto overflow-hidden bg-gray-100 observe-me slide-right">
          <Image
            src="/design-images/resized/1764870319083-DS1_4000.jpg"
            alt="Unique suite with barrel tub"
            fill
            className="object-cover image-breathe hover:scale-110 transition-transform duration-700"
          />
        </div>
      </section>

      {/* Spa Section - Asymmetric Layout (Image 3/5, Text 2/5) with Spa Logo */}
      <section className="grid lg:grid-cols-5 min-h-screen design-section-gap rounded-3xl overflow-hidden">
        {/* Image Side - 3 columns */}
        <div className="lg:col-span-3 relative h-[50vh] lg:h-auto overflow-hidden bg-gray-100 order-2 lg:order-1 observe-me slide-left">
          <Image
            src="/design-images/resized/1764858916694-DS1_3737.jpg"
            alt="Historic spa pool"
            fill
            className="object-cover image-breathe hover:scale-110 transition-transform duration-700"
          />
          {/* Spa Logo - Bottom Right */}
          <div className="absolute bottom-3 right-6 md:bottom-4 md:right-8 bg-black/40 backdrop-blur-sm rounded-xl px-3 py-2">
            <Image
              src="/design-images/logos/cellarium-spa-transparent.png"
              alt="Cellarium SPA"
              width={63}
              height={25}
              className="w-[63px] opacity-90"
            />
          </div>
        </div>

        {/* Text Side - 2 columns */}
        <div className="lg:col-span-2 flex items-center justify-center design-content-pad lg:p-12 xl:p-16 bg-gradient-to-br from-[#e8f4f4] to-white order-1 lg:order-2">
          <div className="max-w-lg observe-me slide-right">
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6 leading-[1.1]">
              <span className="text-[#478B8B]">{t('spa.heading')}</span>
              <br />
              <span className="text-gray-800 text-xl sm:text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
                {t('spa.subheading')}
              </span>
            </h2>
            <div className="w-16 h-1 bg-[#478B8B] mb-6" />
            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed mb-5">
              {t('spa.body1')}
            </p>
            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed mb-8">
              {t('spa.body2')}
            </p>
            <div>
              <Link href="/spa" className="group px-[26px] py-5 xl:py-6 bg-[#478B8B] hover:bg-[#356666] text-white font-serif text-xl xl:text-2xl rounded-full transition-all duration-500 inline-flex items-center gap-3 shadow-lg hover:shadow-xl hover:shadow-[#478B8B]/30">
                {t('spa.cta')}
                <ArrowRight className="w-7 h-7 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Spa Treatment Detail */}
      <section className="relative h-[46vw] overflow-hidden bg-gray-100 observe-me fade-in design-section-gap rounded-3xl">
        <Image
          src="/design-images/resized/1764858943451-DS1_3561-Edit.jpg"
          alt="Spa treatment"
          fill
          className="object-cover image-breathe-slow"
          style={{ objectPosition: 'bottom' }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/80 via-transparent to-transparent" />
      </section>

      {/* Dining Section - Asymmetric Layout (Text 2/5, Image 3/5) */}
      <section className="grid lg:grid-cols-5 min-h-screen design-section-gap rounded-3xl overflow-hidden">
        {/* Text Side - 2 columns */}
        <div className="lg:col-span-2 flex items-center justify-center design-content-pad lg:p-12 xl:p-16 bg-[#faf8f6]">
          <div className="max-w-lg observe-me slide-left">
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6 leading-[1.1]">
              <span className="text-[#801515]">{t('dining.heading')}</span>
              <br />
              <span className="text-gray-800 text-xl sm:text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
                {t('dining.subheading')}
              </span>
            </h2>
            <div className="w-16 h-1 bg-[#801515] mb-6" />
            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed mb-5">
              {t('dining.body1')}
            </p>
            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed mb-8">
              {t('dining.body2')}
            </p>
            <div>
              <Link href="/dining" className="group px-[26px] py-5 xl:py-6 bg-[#801515] hover:bg-[#601010] text-white font-serif text-xl xl:text-2xl rounded-full transition-all duration-500 inline-flex items-center gap-3 shadow-lg hover:shadow-xl hover:shadow-[#801515]/30">
                {t('dining.cta')}
                <ArrowRight className="w-7 h-7 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>

        {/* Image Side - 3 columns */}
        <div className="lg:col-span-3 relative h-[50vh] lg:h-auto overflow-hidden bg-gray-100 observe-me slide-right">
          <Image
            src="/design-images/resized/1764859025033-DS1_2295.jpg"
            alt="Restaurant dining"
            fill
            className="object-cover image-breathe hover:scale-110 transition-transform duration-700"
          />
        </div>
      </section>

      {/* Space to Breathe - Asymmetric Layout (Image 3/5, Text 2/5) */}
      <section className="grid lg:grid-cols-5 min-h-[90vh] design-section-gap rounded-3xl overflow-hidden">
        {/* Image Side - 3 columns */}
        <div className="lg:col-span-3 relative h-[50vh] lg:h-auto overflow-hidden bg-gray-100 order-2 lg:order-1 observe-me slide-left">
          <Image
            src="/design-images/resized/1764859284005-DS1_2814.jpg"
            alt="Outdoor scenic view"
            fill
            className="object-cover image-breathe hover:scale-110 transition-transform duration-700"
            style={{ objectPosition: 'right' }}
          />
        </div>

        {/* Text Side - 2 columns */}
        <div className="lg:col-span-2 flex items-center justify-center design-content-pad lg:p-12 xl:p-16 bg-gradient-to-br from-[#f0f4f0] to-white order-1 lg:order-2">
          <div className="max-w-lg observe-me slide-right">
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6 leading-[1.1]">
              <span className="text-[#4A6741]">{t('breathe.heading')}</span>
            </h2>
            <div className="w-16 h-1 bg-[#4A6741] mb-6" />
            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed">
              {t('breathe.body')}
            </p>
          </div>
        </div>
      </section>

      {/* Space to Discover - Asymmetric Layout (Text 2/5, Image 3/5) */}
      <section className="grid lg:grid-cols-5 min-h-screen design-section-gap rounded-3xl overflow-hidden">
        {/* Text Side - 2 columns */}
        <div className="lg:col-span-2 flex items-center justify-center design-content-pad lg:p-12 xl:p-16 bg-gradient-to-br from-[#f0f4f0] to-[#faf8f6]">
          <div className="max-w-lg observe-me slide-left">
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6 leading-[1.1]">
              <span className="text-[#4A6741]">{t('discover.heading')}</span>
            </h2>
            <div className="w-16 h-1 bg-[#4A6741] mb-6" />
            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed mb-8">
              {t('discover.body')}
            </p>
            <div>
              <Link href="/activities" className="group px-[26px] py-5 xl:py-6 bg-[#4A6741] hover:bg-[#3a5231] text-white font-serif text-xl xl:text-2xl rounded-full transition-all duration-500 inline-flex items-center gap-3 shadow-lg hover:shadow-xl hover:shadow-[#4A6741]/30">
                {t('discover.cta')}
                <ArrowRight className="w-7 h-7 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>

        {/* Image Side - 3 columns */}
        <div className="lg:col-span-3 relative h-[50vh] lg:h-auto overflow-hidden bg-gray-100 observe-me slide-right">
          <Image
            src="/design-images/resized/1764862869488-0025DS1_6118.jpg"
            alt="Cultural exhibition"
            fill
            className="object-cover image-breathe hover:scale-110 transition-transform duration-700"
          />
        </div>
      </section>


      {/* Transformation Section - Asymmetric Layout (Image 3/5, Text 2/5) */}
      <section className="grid lg:grid-cols-5 min-h-screen design-section-gap rounded-3xl overflow-hidden">
        {/* Image Side - 3 columns */}
        <div className="lg:col-span-3 relative h-[50vh] lg:h-auto overflow-hidden bg-gray-100 order-2 lg:order-1 observe-me slide-left">
          <Image
            src="/design-images/resized/1764869761996-DS2_3801.jpg"
            alt="Traditional gown experience"
            fill
            className="object-cover image-breathe hover:scale-110 transition-transform duration-700"
            style={{ objectPosition: '24%' }}
          />
        </div>

        {/* Text Side - 2 columns */}
        <div className="lg:col-span-2 flex items-center justify-center design-content-pad lg:p-12 xl:p-16 bg-white order-1 lg:order-2">
          <div className="max-w-lg observe-me slide-right">
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6 leading-[1.1]">
              <span className="text-[#801515]">{t('return.heading')}</span>
              <br />
              <span className="text-gray-800 text-xl sm:text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
                {t('return.subheading')}
              </span>
            </h2>
            <div className="w-16 h-1 bg-[#801515] mb-6" />
            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed mb-8">
              {t('return.body')}
            </p>
            <div>
              <Link href="/activities#costume" className="group px-[26px] py-5 xl:py-6 bg-[#801515] hover:bg-[#601010] text-white font-serif text-xl xl:text-2xl rounded-full transition-all duration-500 inline-flex items-center gap-3 shadow-lg hover:shadow-xl hover:shadow-[#801515]/30">
                {t('return.cta')}
                <ArrowRight className="w-7 h-7 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* News & Offers - Gift Vouchers & Vote for Us */}
      <div className="design-section-gap mt-8 md:mt-0">
        <NewsAndOffers />
      </div>

      {/* Corporate Section - Asymmetric Layout (Text 2/5, Image 3/5) */}
      <section className="grid lg:grid-cols-5 min-h-screen mt-8 mx-8 rounded-3xl overflow-hidden">
        {/* Text Side - 2 columns */}
        <div className="lg:col-span-2 flex items-center justify-center p-8 lg:p-12 xl:p-16 bg-gradient-to-br from-[#f5f3f0] to-[#faf8f6]">
          <div className="max-w-lg observe-me slide-left">
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6 leading-[1.1]">
              <span className="text-[#801515]">{t('corporate.heading')}</span>
              <br />
              <span className="text-gray-800 text-xl sm:text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
                {t('corporate.subheading')}
              </span>
            </h2>
            <div className="w-16 h-1 bg-[#801515] mb-6" />
            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed mb-5">
              {t('corporate.body1')}
            </p>
            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed mb-8">
              {t('corporate.body2')}
            </p>
            <div>
              <Link href="/corporate" className="group px-[26px] py-5 xl:py-6 bg-[#801515] hover:bg-[#601010] text-white font-serif text-xl xl:text-2xl rounded-full transition-all duration-500 inline-flex items-center gap-3 shadow-lg hover:shadow-xl hover:shadow-[#801515]/30">
                {t('corporate.cta')}
                <ArrowRight className="w-7 h-7 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>

        {/* Image Side - 3 columns */}
        <div className="lg:col-span-3 relative h-[50vh] lg:h-auto overflow-hidden bg-gray-100 observe-me slide-right">
          <Image
            src="/design-images/corporate/castle-wooden-balcony-golden-hour.jpg"
            alt="Corporate retreat at Castle Hotel Daniel"
            fill
            className="object-cover image-breathe hover:scale-110 transition-transform duration-700"
          />
        </div>
      </section>

      {/* Corporate Highlights - 3 Image Grid */}
      <section className="mt-8 mx-8">
        <div className="grid md:grid-cols-3 gap-4 rounded-3xl overflow-hidden">
          {[
            {
              image: "/design-images/corporate/ball-room-conference-u-shape.jpg",
              title: t('corporate.meetingVenues'),
              subtitle: t('corporate.meetingVenuesSub')
            },
            {
              image: "/design-images/corporate/group-dinner-wine-toast.jpg",
              title: t('corporate.thematicDinners'),
              subtitle: t('corporate.thematicDinnersSub')
            },
            {
              image: "/design-images/uploads/1771581615354-ray01892-copy.jpg",
              title: t('corporate.teamActivities'),
              subtitle: t('corporate.teamActivitiesSub')
            }
          ].map((item, i) => (
            <div key={i} className="relative h-80 overflow-hidden group observe-me slide-up" style={{ animationDelay: `${i * 0.15}s` }}>
              <Image
                src={item.image}
                alt={item.title}
                fill
                className="object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-5 right-5">
                <h3 className="text-xl font-bold text-white">{item.title}</h3>
                <p className="text-white/70 text-sm">{item.subtitle}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Events Section - Asymmetric Layout (Image 3/5, Text 2/5) */}
      <section className="grid lg:grid-cols-5 min-h-screen design-section-gap rounded-3xl overflow-hidden">
        {/* Image Side - 3 columns */}
        <div className="lg:col-span-3 relative h-[50vh] lg:h-auto overflow-hidden bg-gray-100 order-2 lg:order-1 observe-me slide-left">
          <Image
            src="/design-images/events/events-6.webp"
            alt="Wedding celebration in the castle event barn"
            fill
            className="object-cover image-breathe hover:scale-110 transition-transform duration-700"
          />
        </div>

        {/* Text Side - 2 columns */}
        <div className="lg:col-span-2 flex items-center justify-center design-content-pad lg:p-12 xl:p-16 bg-white order-1 lg:order-2">
          <div className="max-w-lg observe-me slide-right">
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6 leading-[1.1]">
              <span className="text-[#801515]">{t('events.heading')}</span>
              <br />
              <span className="text-gray-800 text-xl sm:text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
                {t('events.subheading')}
              </span>
            </h2>
            <div className="w-16 h-1 bg-[#801515] mb-6" />
            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed mb-5">
              {t('events.body1')}
            </p>
            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed mb-8">
              {t('events.body2')}
            </p>
            <div>
              <Link href="/events" className="group px-[26px] py-5 xl:py-6 bg-[#801515] hover:bg-[#601010] text-white font-serif text-xl xl:text-2xl rounded-full transition-all duration-500 inline-flex items-center gap-3 shadow-lg hover:shadow-xl hover:shadow-[#801515]/30">
                {t('events.cta')}
                <ArrowRight className="w-7 h-7 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Events Highlights - 3 Image Grid */}
      <section className="design-section-gap">
        <div className="grid md:grid-cols-3 gap-4 rounded-3xl overflow-hidden">
          {[
            {
              image: "/design-images/events/events-1.webp",
              title: t('events.weddings'),
              subtitle: t('events.weddingsSub')
            },
            {
              image: "/design-images/events/events-71.webp",
              title: t('events.privateEvents'),
              subtitle: t('events.privateEventsSub')
            },
            {
              image: "/design-images/events/events-73.webp",
              title: t('events.photoShoots'),
              subtitle: t('events.photoShootsSub')
            }
          ].map((item, i) => (
            <div key={i} className="relative h-80 overflow-hidden group observe-me slide-up" style={{ animationDelay: `${i * 0.15}s` }}>
              <Image
                src={item.image}
                alt={item.title}
                fill
                className="object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-5 right-5">
                <h3 className="text-xl font-bold text-white">{item.title}</h3>
                <p className="text-white/70 text-sm">{item.subtitle}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Reviews — real Google data via Places API (New); see docs/86.
          Server-fetched in [locale]/page.tsx and passed down as a prop. Hidden
          (fail-soft) when there's no data. Featured cards are 4–5★; the
          aggregate shown is Google's true all-in figure (docs/86 §8, option b). */}
      {reviews && reviews.reviews.length > 0 && <HomepageReviews data={reviews} />}

      {/* FAQ Section */}
      <section className="relative py-24 lg:py-32 bg-gradient-to-b from-white to-[#faf8f6] mt-8">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center observe-me slide-up mb-16">
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6">
              <span className="text-[#801515]">{t('faq.heading')}</span>
              <br />
              <span className="text-gray-800 text-xl sm:text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
                {t('faq.subheading')}
              </span>
            </h2>
            <div className="w-16 h-1 bg-[#801515] mx-auto mb-8" />

            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed mb-12">
              {t('faq.description')}
            </p>
          </div>

          <FAQAccordion />
        </div>
      </section>

      {/* Final Embrace - Full Width with CTA */}
      <section className="relative min-h-screen flex items-center justify-center py-32 px-6 bg-gradient-to-b from-white to-[#faf8f6] mt-8">
        <div className="max-w-4xl mx-auto text-center observe-me slide-up">
          <h2 className="font-poppins text-4xl sm:text-7xl md:text-8xl font-bold mb-10 leading-[1.1]">
            <span className="text-[#801515]">{t('embrace.heading')}</span>
          </h2>
          <p className="text-xl sm:text-3xl md:text-4xl text-gray-700 leading-relaxed mb-16">
            {t('embrace.body')}
          </p>
          <div>
            <Link href="/rooms" className="group px-[26px] py-7 bg-[#801515] hover:bg-[#601010] text-white font-serif text-2xl xl:text-3xl rounded-full transition-all duration-500 inline-flex items-center gap-4 mx-auto shadow-xl hover:shadow-2xl hover:shadow-[#801515]/40 transform hover:scale-105">
              {t('embrace.cta')}
              <ArrowRight className="w-8 h-8 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-20 left-20 w-32 h-32 bg-[#801515]/5 rounded-full blur-3xl" style={{ animation: 'breatheSlow 8s ease-in-out infinite' }} />
        <div className="absolute bottom-20 right-20 w-40 h-40 bg-[#478B8B]/5 rounded-full blur-3xl" style={{ animation: 'breatheSlow 10s ease-in-out infinite', animationDelay: '2s' }} />
        <div className="absolute top-1/2 left-1/4 w-3 h-3 bg-[#801515] rounded-full opacity-40" style={{ animation: 'pulse 5s ease-in-out infinite' }} />
        <div className="absolute top-1/3 right-1/4 w-2 h-2 bg-[#4A6741] rounded-full opacity-35" style={{ animation: 'pulse 6s ease-in-out infinite', animationDelay: '1s' }} />
      </section>

      {/* Newsletter Section - Just Above Footer */}
      <section className="grid lg:grid-cols-2 min-h-[60vh] design-section-gap mb-8 rounded-3xl overflow-hidden observe-me fade-in">
        {/* Image Side with Vintage Background */}
        <div className="relative h-[40vh] lg:h-auto overflow-hidden bg-gray-100 order-2 lg:order-1">
          <Image
            src="/design-images/newsletter-plaque.jpg"
            alt="Historic castle plaque"
            fill
            className="object-cover image-breathe"
            style={{ objectPosition: 'center' }}
          />
        </div>

        {/* Newsletter Form Side */}
        <div className="flex items-center justify-center design-content-pad lg:p-12 xl:p-16 bg-gradient-to-br from-[#faf8f6] to-white order-1 lg:order-2">
          <div className="max-w-md w-full">
            <h2 className="font-poppins text-2xl sm:text-4xl md:text-5xl xl:text-6xl font-bold mb-4 leading-[1.1] text-gray-900">
              {t('newsletter.title')}
              <br />
              <span className="text-[#801515]">{t('newsletter.titleAccent')}</span>
            </h2>
            <div className="w-16 h-1 bg-[#801515] mb-6" />
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              {t('newsletter.description')}
            </p>

            <NewsletterSubscribeForm source="landing-page" />
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
